import {
  QueryClient,
  QueryClientConfig,
  QueryKey,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

// React Query configuration optimized for gaming applications
const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // Cache time - how long unused/inactive cache data remains in memory
      gcTime: 1000 * 60 * 5, // 5 minutes (renamed from cacheTime in v5)

      // Stale time - how long data is considered fresh
      staleTime: 1000 * 30, // 30 seconds (aggressive for real-time gaming)

      // Retry configuration for failed requests
      retry: (failureCount, error: unknown) => {
        const axiosError = error as AxiosError;
        // Don't retry on 4xx errors (client errors)
        if (
          axiosError?.response?.status &&
          axiosError.response.status >= 400 &&
          axiosError.response.status < 500
        ) {
          return false;
        }
        // Retry up to 3 times for network/server errors
        return failureCount < 3;
      },

      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch on window focus for real-time updates
      refetchOnWindowFocus: true,

      // Refetch on network reconnect
      refetchOnReconnect: true,

      // Don't refetch on mount if data is fresh
      refetchOnMount: "always",

      // Network mode - fail fast on network errors
      networkMode: "online",
    },
    mutations: {
      // Retry mutations once for network errors
      retry: (failureCount, error: unknown) => {
        const axiosError = error as AxiosError;
        if (
          axiosError?.response?.status &&
          axiosError.response.status >= 400 &&
          axiosError.response.status < 500
        ) {
          return false;
        }
        return failureCount < 1;
      },

      // Network mode for mutations
      networkMode: "online",
    },
  },
};

// Create the query client
export const queryClient = new QueryClient(queryClientConfig);

// Query invalidation patterns for game events
export const invalidationPatterns = {
  // Clear all cache (nuclear option)
  clearAllCache: () => {
    queryClient.clear();
  },
};

// Cache management utilities
export const cacheUtils = {
  // Set specific data in cache
  setQueryData: <T>(queryKey: QueryKey, data: T) => {
    queryClient.setQueryData(queryKey, data);
  },

  // Get specific data from cache
  getQueryData: <T>(queryKey: QueryKey): T | undefined => {
    return queryClient.getQueryData<T>(queryKey);
  },

  // Remove specific query from cache
  removeQuery: (queryKey: QueryKey) => {
    queryClient.removeQueries({ queryKey });
  },

  // Cancel outgoing queries (useful when component unmounts)
  cancelQueries: (queryKey: QueryKey) => {
    queryClient.cancelQueries({ queryKey });
  },

  // Get cache size and stats
  getCacheStats: () => {
    const cache = queryClient.getQueryCache();
    return {
      totalQueries: cache.getAll().length,
      queries: cache.getAll().map((query) => ({
        queryKey: query.queryKey,
        state: query.state.status,
        dataUpdatedAt: query.state.dataUpdatedAt,
        // staleTime: query.options.staleTime, // Property doesn't exist on QueryOptions
      })),
    };
  },
};

// Error handling utilities
export const errorUtils = {
  // Check if error is a network error
  isNetworkError: (error: unknown): boolean => {
    const axiosError = error as AxiosError;
    return !axiosError?.response && axiosError?.code !== "ECONNABORTED";
  },

  // Check if error is a server error (5xx)
  isServerError: (error: unknown): boolean => {
    const axiosError = error as AxiosError;
    return axiosError?.response?.status
      ? axiosError.response.status >= 500
      : false;
  },

  // Check if error is a client error (4xx)
  isClientError: (error: unknown): boolean => {
    const axiosError = error as AxiosError;
    const status = axiosError?.response?.status;
    return status ? status >= 400 && status < 500 : false;
  },

  // Get user-friendly error message
  getErrorMessage: (error: unknown): string => {
    const axiosError = error as AxiosError;
    if (errorUtils.isNetworkError(error)) {
      return "Network connection failed. Please check your internet connection.";
    }

    if (errorUtils.isServerError(error)) {
      return "Server error occurred. Please try again later.";
    }

    if (
      axiosError?.response?.data &&
      typeof axiosError.response.data === "object" &&
      "error" in axiosError.response.data
    ) {
      const errorData = axiosError.response.data as {
        error?: { message?: string };
      };
      if (errorData.error?.message) {
        return errorData.error.message;
      }
    }

    if (axiosError?.message) {
      return axiosError.message;
    }

    return "An unexpected error occurred. Please try again.";
  },
};

export default queryClient;
